import { cn } from '@/lib/utils'

export type SelectVariant = 'default' | 'filter' | 'inline'

interface SelectTriggerState {
  variant: SelectVariant
  isOpen: boolean
  hasError: boolean
  isDisabled: boolean
  /** `filter` only: a non-default value is applied. */
  active: boolean
}

/**
 * Trigger button classes per `Select` variant. No variant uses a brand focus ring:
 * keyboard focus and the open state show a dark ink border instead (violet stays
 * for the selected-row check only). Each disabled look is also keyed on `:disabled`,
 * so a trigger disabled by an enclosing `<fieldset disabled>` dims like its neighbours.
 */
export function selectTriggerClasses({ variant, isOpen, hasError, isDisabled, active }: SelectTriggerState): string {
  const base = cn(
    'flex items-center justify-between text-left text-[16px] text-clovie-text',
    'border transition-colors duration-150 focus:outline-none'
  )

  if (variant === 'filter') {
    // Pill filter chip — stays neutral in every state (no violet border or fill);
    // an applied value only reads a touch bolder.
    return cn(
      base,
      'h-11 w-full rounded-[var(--r-pill)] px-4 bg-clovie-surface',
      'hover:bg-clovie-hover focus-visible:border-clovie-text',
      isOpen ? 'border-clovie-text' : 'border-clovie-border-strong',
      active && 'font-medium',
      hasError && 'border-form-error hover:border-form-error',
      'disabled:cursor-not-allowed disabled:opacity-50',
      isDisabled && 'cursor-not-allowed opacity-50'
    )
  }

  if (variant === 'inline') {
    // Soft content-width pill; white with an ink border while open or keyboard-focused.
    return cn(
      base,
      'h-11 w-auto max-w-full gap-2 rounded-[var(--r-pill)] pl-3.5 pr-3 font-medium',
      'focus-visible:border-clovie-text focus-visible:bg-clovie-surface',
      isOpen ? 'border-clovie-text bg-clovie-surface' : 'border-transparent bg-clovie-sunk hover:bg-[var(--sand-200)]',
      hasError && 'border-form-error',
      'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-clovie-sunk',
      isDisabled && 'cursor-not-allowed opacity-50 hover:bg-clovie-sunk'
    )
  }

  // Same field as Input: 44px, white, sand-300 border that darkens on hover.
  return cn(
    base,
    'h-11 w-full gap-2.5 rounded-[var(--r-md)] pl-3.5 pr-3 bg-clovie-input',
    'hover:border-[var(--sand-400)] focus-visible:border-clovie-text',
    isOpen ? 'border-clovie-text' : 'border-clovie-border-strong',
    hasError && 'border-form-error hover:border-form-error focus-visible:border-form-error',
    'disabled:cursor-not-allowed disabled:bg-clovie-sunk disabled:text-clovie-muted disabled:border-clovie-border disabled:hover:border-clovie-border',
    isDisabled && 'cursor-not-allowed bg-clovie-sunk text-clovie-muted border-clovie-border hover:border-clovie-border'
  )
}
